import React, { useEffect, useState } from "react";
import {
  Button,
  H2,
  LabeledSelect,
  Popup,
  PopupHeader,
  SkubotSpinner,
  Typography,
  XIcon,
} from "@commonsku/styles";
import { BASE_ZINDEX } from "../../../popup-factory";
import styled from "styled-components";
import { oauth } from "../../../utils";

interface ExportToShipStationPopupProps {
  ordersIds: string[];
  handleShowPopup: (
    popup: string,
    errorMessage?: string | null,
    getStoresError?: boolean
  ) => void;
}

interface ShipStationStore {
  storeId: string;
  storeName: string;
}

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  gap: 10px;
  margin-bottom: 16px !important;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ExportToShipStationPopup = ({
  ordersIds,
  handleShowPopup,
}: ExportToShipStationPopupProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [formattedStoreList, setFormattedStoreList] = useState<
    Array<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const storeList = await oauth("GET", "shipstation-stores", {});

        const formattedStoreList = storeList.json.map(
          (store: ShipStationStore) => ({
            value: store.storeId,
            label: store.storeName,
          })
        );
        setFormattedStoreList(formattedStoreList);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to fetch stores:", err);
        handleShowPopup("ExportToShipStationStatusPopup", null, true);
      }
    };

    fetchStores();
  }, [handleShowPopup]);

  const handleChange = (
    selectedStore: { value: string; label: string } | null
  ) => {
    setSelectedStore(selectedStore ? selectedStore : null);
  };

  const handleExport = async () => {
    setIsLoading(true);
    try {
      await oauth("POST", "shipstation-export", {
        order_ids: ordersIds,
        shipstation_store_id: selectedStore?.value,
      });
      handleShowPopup("ExportToShipStationStatusPopup", null);
    } catch (err) {
      handleShowPopup(
        "ExportToShipStationStatusPopup",
        err?.json?.errors[0] ||
          "An error occurred while exporting to ShipStation. Please try again."
      );
    }
  };

  return (
    <>
      <Popup
        overlayZIndex={BASE_ZINDEX + 100}
        zIndex={BASE_ZINDEX + 200}
        style={{ width: 575, height: 418, borderRadius: 10 }}
        header={
          <PopupHeader
            style={{ padding: 0, alignItems: "center", marginBottom: 16 }}
          >
            <H2
              style={{
                fontSize: 24,
                lineHeight: "40px",
                margin: "0px",
              }}
              className="title"
            >
              Select Destination Store
            </H2>
            <XIcon
              style={{ cursor: "pointer" }}
              size="large"
              onClick={() => handleShowPopup("")}
            />
          </PopupHeader>
        }
        noHeader={isLoading}
        noCloseButton
        popupContentStyle={{ width: isLoading && "100%" }}
      >
        {isLoading ? (
          <SkubotSpinner
            size="medium"
            style={{ margin: "auto", width: "100%", height: "100%" }}
          />
        ) : (
          <StyledContainer>
            <>
              <SelectContainer>
                <Typography.Text
                  style={{ lineHeight: "24px", marginBottom: 0 }}
                >
                  If you want to connect to a store that is not listed here,
                  please create a new store in ShipStation.
                </Typography.Text>
                <LabeledSelect
                  placeholder="Select Destination Store"
                  inPopup
                  isSearchable
                  isClearable
                  label="Destination Store"
                  options={formattedStoreList}
                  value={selectedStore}
                  maxMenuHeight={146}
                  onChange={(e) => handleChange(e)}
                  noOptionsMessage={() =>
                    "No ShipStation store found, please create one in ShipStation."
                  }
                />
              </SelectContainer>
              <ButtonContainer>
                <Button
                  variant={"secondary"}
                  disabled={isLoading}
                  onClick={() => handleShowPopup("")}
                  size="medium"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleExport}
                  size="medium"
                  disabled={!selectedStore?.value || isLoading}
                  style={{
                    cursor:
                      (!selectedStore?.value || isLoading) && "not-allowed",
                  }}
                >
                  {isLoading ? (
                    <SkubotSpinner
                      size="button"
                      skubot={false}
                      style={{ margin: "auto" }}
                    />
                  ) : (
                    "Export"
                  )}
                </Button>
              </ButtonContainer>
            </>
          </StyledContainer>
        )}
      </Popup>
    </>
  );
};

export default ExportToShipStationPopup;
